import {
  Badge,
  Divider,
  HStack,
  Text,
  useClipboard,
  VStack,
} from 'native-base';
import { toast } from 'react-toastify';
import Button from 'ui/components/Button';
import Icon from 'ui/components/Icon';
import { safeDateFormat } from 'ui/utils/formatDates';

import { colors } from '~/theme/colors';
import { IOrganization } from '~/types/interfaces/organization';

import { UserProfilePopulated } from '../../../../../../types/interfaces/userProfile';

interface CardInfoProps {
  icon: string;
  label: string;
  value?: string;
}

const CardInfo = ({ label, icon, value }: CardInfoProps) => {
  return (
    <VStack space={1}>
      <Text color="gray.500" fontSize={'xs'} fontWeight={500}>
        {label}
      </Text>
      <HStack space={2} alignItems="center">
        <Icon icon={icon} size={20} color={colors.muted[400]} />
        <Text fontSize="md" color="darkText">
          {value || '-'}
        </Text>
      </HStack>
    </VStack>
  );
};

export const MemberDetails = ({
  member,
}: {
  member?: UserProfilePopulated;
  organization?: IOrganization;
}) => {
  const { onCopy } = useClipboard();

  const handleCopy = (value?: string) => {
    if (!value) return;
    onCopy(value);
    toast.success('Copied to clipboard');
  };

  return (
    <VStack
      bgColor="singletons.white"
      borderTopRadius={24}
      borderBottomRadius={24}
      p={6}
    >
      <VStack>
        <Text mb={4} fontSize="xl" color="darkText" fontWeight={500}>
          About
        </Text>
        <Divider />
      </VStack>

      <VStack py={6} space={6}>
        <CardInfo
          icon="calendar"
          label="Date of birth"
          value={
            member?.user?.birthDate
              ? safeDateFormat(member.user.birthDate, 'MMMM dd, yyyy')
              : ''
          }
        />
        <CardInfo
          icon="phone"
          label="Phone number"
          value={member?.user?.phoneNumber}
        />
        <HStack alignItems="center" space={3}>
          <CardInfo icon="mail" label="Email" value={member?.user?.email} />
          <Button
            onPress={() => handleCopy(member?.user?.email)}
            _text={{
              color: colors.singletons.black,
            }}
            bgColor="gray.100"
            minW="75px"
            w="75px"
            leftIconName="copy"
            leftIconColor={colors.singletons.black}
          >
            Copy
          </Button>
        </HStack>
        <CardInfo
          icon="map-pin"
          label="Full Address"
          value={member?.user?.preferences?.address?.street}
        />
      </VStack>

      <VStack mt={6}>
        <Text mb={4} fontSize="xl" color="darkText" fontWeight={500}>
          Skills
        </Text>
        <Divider />

        <HStack mt={6} space={3} flexWrap="wrap">
          {member?.user?.preferences?.skills?.map((skill: string) => (
            <Badge
              key={skill}
              mt={2}
              color="info.800"
              fontSize="sm"
              fontWeight={500}
              borderRadius={100}
            >
              {skill}
            </Badge>
          ))}

          {!member?.user?.preferences?.skills?.length ? (
            <Text color="muted.400">No skills registered yet</Text>
          ) : null}
        </HStack>
      </VStack>
    </VStack>
  );
};
